import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./SupportPage.module.css";
import { getFunctions, httpsCallable } from "firebase/functions";

function SupportPage() {
  const [activeCategory, setActiveCategory] = useState("general");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "general",
    message: ""
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const faqCategories = [
    { id: "general", name: "General" },
    { id: "account", name: "Account & Billing" },
    { id: "teams", name: "Teams & Rosters" },
    { id: "stats", name: "Stats & Analytics" },
    { id: "technical", name: "Technical Issues" }
  ];

  const faqs = {
    general: [
      {
        id: "g1",
        question: "What is SportsHive?",
        answer: "SportsHive is a comprehensive sports league management platform that helps teams and leagues organize players, schedules, stats, and more. Our tools simplify management tasks so you can focus on the game."
      },
      {
        id: "g2",
        question: "What sports does SportsHive support?",
        answer: "SportsHive supports all team-based sports including basketball, soccer, volleyball, baseball, football, hockey, and more. Our flexible platform can be adapted to virtually any team sport."
      },
      {
        id: "g3",
        question: "Do I need to pay to use SportsHive?",
        answer: "SportsHive offers both free and premium subscription tiers. The free tier includes basic features like team creation and simple scheduling, while our premium features include advanced analytics, media storage, and expanded roster capabilities."
      }
    ],
    account: [
      {
        id: "a1",
        question: "How do I create an account?",
        answer: "To create an account, click the 'Sign Up' button in the top-right corner of the homepage. Fill out the registration form with your information, and you'll receive a verification email to confirm your account."
      },
      {
        id: "a2",
        question: "How do I upgrade to a premium subscription?",
        answer: "You can upgrade to a premium subscription from your account settings page. Click on 'My Account' in the navigation menu, then select the 'Subscription' tab and choose the plan that fits your needs."
      },
      {
        id: "a3",
        question: "Can I cancel my subscription?",
        answer: "Yes, you can cancel your subscription at any time from the 'My Account' page. Your premium features will remain active until the end of your current billing period."
      }
    ],
    teams: [
      {
        id: "t1",
        question: "How many teams can I create?",
        answer: "Free accounts can create 1 team with up to 15 players. Premium accounts can create up to 5 teams with 100 players each."
      },
      {
        id: "t2",
        question: "How do I invite players to my team?",
        answer: "Go to your team's roster page and click 'Add Player'. You can either enter player information manually or send email invitations for players to join the team with their own accounts."
      },
      {
        id: "t3",
        question: "Can I transfer team ownership to someone else?",
        answer: "Yes, team administrators can transfer ownership from the team settings page. The new owner will need to have a SportsHive account and must accept the transfer request."
      }
    ],
    stats: [
      {
        id: "s1",
        question: "What statistics does SportsHive track?",
        answer: "SportsHive tracks both basic and advanced statistics depending on the sport. Common metrics include points, assists, rebounds, and steals for basketball, as well as sport-specific stats for other games."
      },
      {
        id: "s2",
        question: "Can I export team and player statistics?",
        answer: "Yes, premium accounts can export statistics in CSV or PDF format from the team stats page."
      },
      {
        id: "s3",
        question: "How do I record game statistics?",
        answer: "After creating a game in your schedule, you can record statistics by clicking on the game and selecting 'Record Stats'. You can update stats during or after the game."
      }
    ],
    technical: [
      {
        id: "tech1",
        question: "What browsers does SportsHive support?",
        answer: "SportsHive works with all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend keeping your browser updated to the latest version."
      },
      {
        id: "tech2",
        question: "Is there a mobile app available?",
        answer: "Currently, SportsHive is a web-based platform optimized for both desktop and mobile browsers. A dedicated mobile app is in development and will be released soon."
      },
      {
        id: "tech3",
        question: "How do I reset my password?",
        answer: "Click on 'Forgot Password' on the login page and enter your email address. You'll receive a password reset link that is valid for 24 hours."
      }
    ]
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm({
      ...contactForm,
      [name]: value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    // Validate form
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setFormError("Please fill out all required fields");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email)) {
      setFormError("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);
    setFormError(null);
    
    try {
      // Get a reference to the Firebase Functions
      const functions = getFunctions();
      
      // Create the sendSupportEmail callable - using the exact approach as invitation emails
      const sendSupportEmail = httpsCallable(functions, 'sendSupportEmail');
      
      // Using the simplified approach that matches your RosterAddPage
      const result = await sendSupportEmail({ 
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message
      });
      
      console.log("Support request submitted successfully:", result);
      
      // Handle successful submission
      setFormSubmitted(true);
      
      // Reset form after successful submission
      setContactForm({
        name: "",
        email: "",
        subject: "general",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting support request:", error);
      
      // Enhanced error logging for debugging
      if (error.details) {
        console.error("Error details:", error.details);
      }
      
      // Properly extract and display the error message
      let errorMessage = "There was an error submitting your request. Please try again later.";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.details && error.details.message) {
        errorMessage = error.details.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.supportContainer}>
      <div className={styles.supportHeader}>
        <h1>SportsHive Support Center</h1>
        <p>Find answers to common questions or reach out to our support team for assistance</p>
      </div>
      
      <div className={styles.supportContent}>
        <section className={styles.faqSection}>
          <h2>Frequently Asked Questions</h2>
          
          <div className={styles.faqCategories}>
            {faqCategories.map(category => (
              <button
                key={category.id}
                className={`${styles.categoryButton} ${activeCategory === category.id ? styles.activeCategory : ''}`}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSelectedQuestion(null);
                }}
              >
                {category.name}
              </button>
            ))}
          </div>
          
          <div className={styles.faqList}>
            {faqs[activeCategory].map(faq => (
              <div key={faq.id} className={styles.faqItem}>
                <div 
                  className={styles.faqQuestion}
                  onClick={() => setSelectedQuestion(selectedQuestion === faq.id ? null : faq.id)}
                >
                  <h3>{faq.question}</h3>
                  <span className={styles.faqToggle}>
                    {selectedQuestion === faq.id ? '−' : '+'}
                  </span>
                </div>
                
                {selectedQuestion === faq.id && (
                  <div className={styles.faqAnswer}>
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
        
        <section className={styles.contactSection}>
          <h2>Contact Support</h2>
          <p>Can't find what you're looking for? Our support team is here to help.</p>
          
          {formSubmitted ? (
            <div className={styles.formSuccess}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.successIcon}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h3>Message Sent!</h3>
              <p>Thank you for reaching out. We'll get back to you as soon as possible.</p>
              <p>A copy of your message has been sent to your email address.</p>
            </div>
          ) : (
            <form className={styles.contactForm} onSubmit={handleContactSubmit}>
              {formError && (
                <div className={styles.formError}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.errorIcon}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <p>{formError}</p>
                </div>
              )}
              
              <div className={styles.formGroup}>
                <label htmlFor="name">Name <span className={styles.required}>*</span></label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={contactForm.name}
                  onChange={handleContactChange}
                  required
                  placeholder="Your name"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="email">Email <span className={styles.required}>*</span></label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={contactForm.email}
                  onChange={handleContactChange}
                  required
                  placeholder="Your email address"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="subject">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={contactForm.subject}
                  onChange={handleContactChange}
                >
                  <option value="general">General Inquiry</option>
                  <option value="account">Account & Billing</option>
                  <option value="technical">Technical Support</option>
                  <option value="feedback">Feedback</option>
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="message">Message <span className={styles.required}>*</span></label>
                <textarea
                  id="message"
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactChange}
                  required
                  placeholder="How can we help you?"
                  rows={5}
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

export default SupportPage;